import React from 'react';
import { Link } from 'react-router-dom';
import { formatLastActive } from '../../utils/utils';
import { formatDate } from '../free-grants/formatDate';
import IdentityCell from './IdentityCell';
import StatusPill from './StatusPill';

/**
 * Presentational users table. Data-fetching/state stays in the UserList
 * container; this component only renders headers, sortable affordances, the
 * identity cell, the accessible status pill, and the drill-down links.
 *
 * Props: { users, columnVisibility, sortField, sortOrder, onSort }
 */

// Parse managing_condition which may arrive as an array or a JSON string.
const renderManagingCondition = (raw) => {
  try {
    const c = typeof raw === 'string' ? JSON.parse(raw) : raw;
    return Array.isArray(c) && c.length > 0 ? c.join(', ') : '-';
  } catch {
    return '-';
  }
};

const SortHeader = ({ field, label, sortField, sortOrder, onSort }) => {
  const active = sortField === field;
  const caret = active ? (sortOrder === 'ASC' ? '▲' : '▼') : '↕';
  return (
    <th className="uv-th-sort" onClick={() => onSort(field)}>
      {label}
      <span className={`uv-caret ${active ? 'uv-caret--on' : ''}`} aria-hidden="true">{caret}</span>
    </th>
  );
};

const UsersTable = ({
  users = [], columnVisibility, sortField, sortOrder, onSort,
  selectedIds, onToggleRow, onTogglePage, selectable = false, avatarUrls,
  onGrantAccess,
}) => {
  const cv = columnVisibility;
  // App-access drill-down now lives as a tab on User 360 rather than its own page.
  // `tab=` selects it; `event_type` pre-picks New Open / Resume. No window is carried:
  // access_count / new_open / resume are ALL-TIME counts, so the log must be all-time
  // too or the drill-down would not reconcile with the number that was clicked.
  const appAccessLink = (id, eventType = '') => {
    const p = new URLSearchParams({ tab: 'app-access' });
    if (eventType) p.set('event_type', eventType);
    return `/user-detail/${btoa(id)}?${p.toString()}`;
  };
  // Alerts drill into the same User 360 screen rather than the standalone
  // /heart-rate-sessions/:id page. `tab=` is named explicitly even though
  // 'heart-rate' is User 360's default: the page restores the last-viewed tab from
  // session storage, so an unnamed link would land wherever the admin was last.
  // No window is carried — alert_count is all-time and User 360 opens all-time (046),
  // so the tab's total reconciles with the number that was clicked.
  const alertsLink = (id) => `/user-detail/${btoa(id)}?tab=heart-rate`;
  const selected = selectedIds || new Set();
  const allPageSelected = selectable && users.length > 0 && users.every((u) => selected.has(u.id));

  return (
    // `uv-table--keep-rows` opts out of main.css's <576px card layout: phones get the same
    // table as a laptop, scrolled sideways, with the User column pinned (users-redesign.css).
    <div className="uv-table-scroll uv-table--keep-rows fullwidth-table track-table-body staff-listing-admin-table-body">
      <table className="table is-striped is-fullwidth">
        <thead>
          <tr>
            {selectable && (
              <th style={{ width: 34 }}>
                <input type="checkbox" aria-label="Select all on page" checked={allPageSelected} onChange={() => onTogglePage && onTogglePage(users)} />
              </th>
            )}
            <th>No</th>
            {cv.name && <th className="uv-col-user">User</th>}
            {cv.watch_name && <th>Watch Name</th>}
            {cv.data_source && <th>Data Sources</th>}
            {cv.platform && <th>Platform</th>}
            {cv.mobile_app_version && <th>Mobile App Version</th>}
            {cv.watch_app_version && <th>Watch App Version</th>}
            {cv.plan_info && <th>Subscription</th>}
            {/* Free access grant (065). Sits directly after Subscription — the plan pill is the
                thing an admin reads before deciding to grant, so the action belongs beside it
                rather than at the far right of a wide, horizontally-scrolled table. Its position
                is fixed: it does not move when Subscription is hidden from the Columns menu. */}
            {onGrantAccess && <th>Grant Free Access</th>}
            {cv.subscription && <th>Plan</th>}
            {cv.last_seen && <SortHeader field="last_active" label="Last Active (UTC)" sortField={sortField} sortOrder={sortOrder} onSort={onSort} />}
            {cv.created_at && <SortHeader field="created_at" label="Registered Date (UTC)" sortField={sortField} sortOrder={sortOrder} onSort={onSort} />}
            {cv.dashboard_layout && <th>Dashboard Layout</th>}
            {cv.managing_condition && <th>Managing Condition</th>}
            {cv.access_count && <SortHeader field="access_count" label="Activity Count" sortField={sortField} sortOrder={sortOrder} onSort={onSort} />}
            {cv.active_last_30_days && <SortHeader field="active_last_30_days" label="Active in Last 30 Days" sortField={sortField} sortOrder={sortOrder} onSort={onSort} />}
            {cv.alert_count && <SortHeader field="alert_count" label="Alerts" sortField={sortField} sortOrder={sortOrder} onSort={onSort} />}
            {cv.new_open && <SortHeader field="new_open" label="New Open" sortField={sortField} sortOrder={sortOrder} onSort={onSort} />}
            {cv.resume && <SortHeader field="resume" label="Resume" sortField={sortField} sortOrder={sortOrder} onSort={onSort} />}
          </tr>
        </thead>
        <tbody>
          {users.map((user, index) => (
            <tr key={user.id} className={selected.has(user.id) ? 'uv-row--selected' : undefined}>
              {selectable && (
                <td>
                  <input
                    type="checkbox"
                    aria-label={`Select ${user.name || user.email || user.id}`}
                    checked={selected.has(user.id)}
                    onChange={() => onToggleRow && onToggleRow(user.id)}
                  />
                </td>
              )}
              <td data-label="No">{index + 1}</td>
              {cv.name && (
                <td data-label="User" className="uv-col-user">
                  <Link
                    to={`/user-detail/${btoa(user.id)}`}
                    style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}
                    title="Open user profile"
                    data-testid="user-360-link"
                  >
                    <IdentityCell name={user.name} email={user.email} avatarUrl={avatarUrls && avatarUrls[user.id]} />
                  </Link>
                </td>
              )}
              {cv.watch_name && <td data-label="Watch Name">{user.watch_name || '-'}</td>}
              {cv.data_source && <td data-label="Data Sources">{user.data_source || '-'}</td>}
              {cv.platform && <td data-label="Platform">{user.platform || '-'}</td>}
              {cv.mobile_app_version && <td data-label="Mobile App Version">{user.mobile_app_version || '-'}</td>}
              {cv.watch_app_version && <td data-label="Watch App Version">{user.watch_app_version || '-'}</td>}
              {cv.plan_info && (
                <td data-label="Subscription">
                  <StatusPill state={user.subscription_state ?? user.subscription_status} trialDays={user.trial_days_remaining} />
                </td>
              )}
              {onGrantAccess && (
                <td data-label="Grant Free Access">
                  <button
                    type="button"
                    className="fg-row-action"
                    onClick={() => onGrantAccess(user)}
                    data-testid="grant-free-access"
                  >
                    Grant Free Access
                  </button>
                  {/* When this member was last GIVEN free access — the question an admin asks
                      right before granting again ("did we already do this?"), so it belongs
                      under the button rather than in a column of its own. Covers revoked and
                      expired grants too: it records what we did, not what is still live. */}
                  <div className="fg-row-last-grant" data-testid="last-free-access-date">
                    {user.last_free_access_granted_at
                      ? `Last provided: ${formatDate(user.last_free_access_granted_at)}`
                      : 'Not provided yet'}
                  </div>
                </td>
              )}
              {cv.subscription && <td data-label="Plan">{user.subscription_plan || '-'}</td>}
              {cv.last_seen && <td data-label="Last Active (UTC)">{user.last_active ? formatLastActive(user.last_active) : '-'}</td>}
              {cv.created_at && <td data-label="Registered Date (UTC)">{formatLastActive(user.registered_time_formatted)}</td>}
              {cv.dashboard_layout && <td data-label="Dashboard Layout">{user.dashboard_layout || '-'}</td>}
              {cv.managing_condition && <td data-label="Managing Condition">{renderManagingCondition(user.managing_condition)}</td>}
              {cv.access_count && <td data-label="Activity Count"><Link to={appAccessLink(user.id)}>{user.access_count}</Link></td>}
              {cv.active_last_30_days && <td data-label="Active in Last 30 Days">{user.active_last_30_days || 0}</td>}
              {cv.alert_count && <td data-label="Alerts"><Link to={alertsLink(user.id)}>{user.alert_count}</Link></td>}
              {cv.new_open && (
                <td data-label="New Open">
                  <Link to={appAccessLink(user.id, '1')}>{user.new_open}</Link>
                </td>
              )}
              {cv.resume && (
                <td data-label="Resume">
                  <Link to={appAccessLink(user.id, '2')}>{user.resume}</Link>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default UsersTable;
